import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";

type Props = {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  onDismiss: () => void;
  src: string;
  title: string;
  dataMzAccent?: string;
  viewHeaderAccent: string;
  viewHeader: string;
  viewTextAccent: string;
  viewText: string;
  viewDate: string;
  icon: ReactNode;
  pending?: boolean;
};

export default function InviteModal({
  open,
  onClose,
  onApply,
  onDismiss,
  src,
  title,
  dataMzAccent,
  viewHeaderAccent,
  viewHeader,
  viewTextAccent,
  viewText,
  viewDate,
  icon,
  pending,
}: Props) {
  const { t } = useTranslation("offer");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      classNameTitle="flex justify-center"
      className="w-[27.5rem] py-7.5 pl-7.5 pr-7"
      style={{ boxShadow: "0px 0px 200px 0px rgba(0, 121, 194, 0.30)" }}
      dataMzAccent={dataMzAccent}
    >
      <div className="relative min-h-[32.875rem]">
        {pending && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-dark-900">
            <Loader />
          </div>
        )}

        <div className="relative w-full h-75 rounded-[0.875rem] overflow-clip">
          <div className="absolute top-0 left-0 size-full">
            {!src && <div className="mz-loader" />}
            <div
              className="opacity-20"
              style={{
                backgroundImage: `url(${src})`,
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                filter: "blur(4px)",
                WebkitFilter: "blur(4px)",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            />
            <div
              className="absolute top-0 left-0 size-full"
              style={{
                background:
                  "radial-gradient(72.11% 100% at 50% 0%, rgba(18, 18, 18, 0) 0%, rgba(21, 21, 21, 0.2) 50%, #181818 100%)",
              }}
            />
            <div
              className="absolute top-0 left-0 size-full"
              style={{
                background:
                  "radial-gradient(72.11% 100% at 50% 0%, rgba(18, 18, 18, 0) 0%, rgba(18, 18, 18, 0.2) 50%, #161719 100%)",
              }}
            />
          </div>
          <div className="relative z-1 flex flex-col gap-5 items-center size-full">
            <div
              className="w-full h-0.5 mb-5"
              style={{
                background:
                  "linear-gradient(90deg, rgba(73, 158, 255, 0) 0%, rgba(73, 158, 255, 0.05) 5%, rgba(73, 158, 255, 1) 50%, rgba(73, 158, 255, 0.05) 95%, rgba(73, 158, 255, 0) 100%)",
              }}
            />
            {icon}
            <div>
              <p className="text-14 text-center text-accent-solid font-bold uppercase">
                {viewHeaderAccent}
              </p>
              <p className="text-14 text-center font-medium uppercase">{viewHeader}</p>
            </div>
            <p className="max-w-[18rem] text-16 font-medium text-center leading-none">{viewText}</p>
            <p className="text-14 text-accent-solid font-bold uppercase">{viewTextAccent}</p>
            <div className="flex items-end gap-1">
              <p className="text-14 text-white/50 font-medium">
                {t("offer.inviteModal.contractDate")}:
              </p>
              <p>{viewDate}</p>
            </div>
          </div>
        </div>
        <div className="flex justify-center gap-2 py-7.5">
          <div className="size-1.5 rounded-md bg-white/10" />
          <div className="size-1.5 rounded-md bg-white/10" />
          <div className="size-1.5 rounded-md bg-white/10" />
        </div>
        <Icon name="signature" className="relative z-1 top-3.5 w-[9.5rem] h-20 mx-auto" />
        <div
          className="relative -top-5 w-full h-px"
          style={{
            background: "linear-gradient(to right, transparent 0%, #191919 50%, transparent 100%)",
          }}
        />
        <p className="mb-5 text-15 text-center font-medium text-white/30 leading-none">
          {t("offer.inviteModal.pushSignature")}
        </p>
        <div className="grid grid-cols-2 w-full gap-2">
          <Button onClick={onApply} className="h-11" loading={pending} disabled={pending}>
            {t("offer.inviteModal.sign")}
          </Button>
          <Button
            variant="soft"
            color="matched"
            onClick={onDismiss}
            className="h-11"
            loading={pending}
            disabled={pending}
          >
            {t("offer.inviteModal.decline")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
